import { CheckCircle2, Info } from 'lucide-react';
import { useQuery, skipToken } from '@tanstack/react-query';
import { Link } from 'react-router-dom';

import { messages } from '@/shared/constants/messages';
import { queryKeys } from '@/shared/lib/queryKeys';
import { cn } from '@/shared/lib/utils';

interface AfterDecisionProps {
  /** `mine`: esta pessoa decidiu. `lost`: alguém decidiu primeiro (§5.4). */
  outcome: 'mine' | 'lost';
  /** A linha do cabeçalho — "Aprovado por você em 13/08" —, já pronta. */
  attribution: string | null;
  /** Quem chegou primeiro, quando a linha do tempo já trouxe o nome. */
  deciderName?: string | null;
}

/**
 * Decidir não pode terminar no vazio (decisão 17).
 *
 * Registrada a decisão, o painel some — o projeto saiu da fila — e no lugar
 * dele entra este bloco, com o caminho de volta e quantos ainda esperam. Sem
 * ele, o revisor que decide dez projetos numa sentada perde o fio a cada
 * decisão e volta pela sidebar, que não conta nada sobre o que acabou de fazer.
 *
 * ⚠️ **"Próximo projeto" NÃO existe aqui, e a ausência é decisão do autor**:
 * decidir em série sem voltar à fila era exatamente o hábito que ela criaria.
 */
export function AfterDecision({ outcome, attribution, deciderName }: AfterDecisionProps) {
  const remaining = useReviewQueueLength();
  const Icon = outcome === 'mine' ? CheckCircle2 : Info;
  // Perder a corrida não é "decisão registrada" — é a decisão de outra pessoa
  // aparecendo na sua tela. E ela NÃO manda recarregar nada (decisão 26).
  const title =
    outcome === 'mine'
      ? messages.review.decisionRecorded
      : deciderName
        ? messages.review.decidedFirstBy(deciderName)
        : messages.review.decidedFirstByUnknown;

  return (
    <div
      className={cn(
        'animate-rise-in space-y-3 rounded-lg border p-4',
        outcome === 'mine' ? 'border-emerald-200 bg-emerald-50' : 'border-border bg-muted/40',
      )}
    >
      <p
        className={cn(
          'flex items-center gap-2 text-sm font-medium',
          outcome === 'mine' ? 'text-emerald-900' : 'text-foreground',
        )}
      >
        <Icon aria-hidden="true" className="size-4 shrink-0" />
        {title}
      </p>

      {attribution ? (
        <p
          className={cn(
            'text-sm',
            outcome === 'mine' ? 'text-emerald-900/90' : 'text-muted-foreground',
          )}
        >
          {attribution}
        </p>
      ) : null}

      <Link
        to="/analise"
        className="inline-flex rounded text-sm font-medium text-primary hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
      >
        {remaining === undefined || remaining === 0
          ? messages.review.backToQueueEmpty
          : messages.review.backToQueue(remaining)}
      </Link>
    </div>
  );
}

/**
 * Quantos ainda esperam decisão — **sem requisição nova** (§2.4).
 *
 * A sidebar mantém `queryKeys.reviewQueue` quente para todo reviewer em
 * qualquer tela; esta tela lê o mesmo cache. `skipToken` é o que diz "assine
 * esta chave, mas nunca busque": sem ele seria ou uma segunda consulta ou um
 * `getQueryData` que não re-renderiza quando a fila encolhe — e a fila encolhe
 * exatamente agora, por causa da decisão que acabou de ser tomada.
 *
 * A chave é de `features/review`, mas o acesso passa por `queryKeys`, que é de
 * `shared/`: importar o hook de lá criaria a dependência `projects → review`,
 * que é o ciclo que os dois barrels existem para impedir.
 */
function useReviewQueueLength(): number | undefined {
  const { data } = useQuery({ queryKey: queryKeys.reviewQueue, queryFn: skipToken });
  return Array.isArray(data) ? data.length : undefined;
}
